import { MIN_AUTO_ZOOM, MAX_AUTO_ZOOM } from "@orbio/shared/constants";
import type { ViewSnap } from "./snapshots.js";

export interface Camera {
  x: number; // world coord at canvas center
  y: number;
  zoom: number; // world units per CSS pixel; smaller = more zoomed in
}

export function computeCamera(view: ViewSnap, ownPid: number, prev: Camera): Camera {
  const owned = view.cells.filter(c => c.pid === ownPid);
  if (owned.length === 0) return prev;
  const cx = owned.reduce((s, c) => s + c.x, 0) / owned.length;
  const cy = owned.reduce((s, c) => s + c.y, 0) / owned.length;
  const mass = owned.reduce((s, c) => s + c.m, 0);
  // zoom: bigger mass = see more world (larger zoom number), but clamped
  // at MAX_AUTO_ZOOM so the view doesn't keep growing endlessly.
  const targetZoom = Math.min(MAX_AUTO_ZOOM, Math.max(MIN_AUTO_ZOOM, Math.sqrt(mass) / 12));
  // Smooth toward target.
  return { x: prev.x + (cx - prev.x) * 0.15, y: prev.y + (cy - prev.y) * 0.15, zoom: prev.zoom + (targetZoom - prev.zoom) * 0.05 };
}

export function render(
  ctx: CanvasRenderingContext2D,
  view: ViewSnap,
  cam: Camera,
  ownPid: number,
  worldW: number,
  worldH: number,
  portals: ReadonlyArray<{ id: number; x: number; y: number }> = [],
): void {
  // ctx is pre-transformed with setTransform(dpr, ...) in main.ts, so
  // user-space dimensions are CSS pixels, not the physical buffer size.
  const rect = ctx.canvas.getBoundingClientRect();
  const w = rect.width;
  const h = rect.height;
  ctx.fillStyle = "#0e1116";
  ctx.fillRect(0, 0, w, h);

  ctx.save();
  ctx.translate(w / 2, h / 2);
  ctx.scale(1 / cam.zoom, 1 / cam.zoom);
  ctx.translate(-cam.x, -cam.y);

  // World bounds
  ctx.strokeStyle = "#2a2f3a";
  ctx.lineWidth = 4 * cam.zoom;
  ctx.strokeRect(0, 0, worldW, worldH);

  const tNow = performance.now() / 1000; // seconds, for animation

  // Portals — animated rotating swirls. Cull to the visible viewport so
  // they don't appear floating off-screen for large/zoomed-out players.
  const PORTAL_RADIUS_CONST = 80;
  const halfW = (w / 2) * cam.zoom;
  const halfH = (h / 2) * cam.zoom;
  for (const portal of portals) {
    if (Math.abs(portal.x - cam.x) > halfW + PORTAL_RADIUS_CONST) continue;
    if (Math.abs(portal.y - cam.y) > halfH + PORTAL_RADIUS_CONST) continue;
    const angle = tNow * 1.5;
    ctx.save();
    ctx.translate(portal.x, portal.y);
    // Outer glow.
    const grad = ctx.createRadialGradient(0, 0, PORTAL_RADIUS_CONST * 0.3, 0, 0, PORTAL_RADIUS_CONST);
    grad.addColorStop(0, "rgba(119,170,255,0.35)");
    grad.addColorStop(1, "rgba(119,170,255,0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(0, 0, PORTAL_RADIUS_CONST, 0, Math.PI * 2);
    ctx.fill();
    // Rotating spokes.
    ctx.strokeStyle = "#9bf";
    ctx.lineWidth = 3 * cam.zoom;
    for (let i = 0; i < 4; i++) {
      const a = angle + (i * Math.PI) / 2;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * PORTAL_RADIUS_CONST * 0.2, Math.sin(a) * PORTAL_RADIUS_CONST * 0.2);
      ctx.lineTo(Math.cos(a) * PORTAL_RADIUS_CONST * 0.85, Math.sin(a) * PORTAL_RADIUS_CONST * 0.85);
      ctx.stroke();
    }
    // Outer arc.
    ctx.beginPath();
    ctx.arc(0, 0, PORTAL_RADIUS_CONST * 0.9, angle, angle + Math.PI * 1.2);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, 0, PORTAL_RADIUS_CONST * 0.9, angle + Math.PI, angle + Math.PI * 2.2);
    ctx.stroke();
    ctx.restore();
  }

  // Food. Green is static, blue and yellow pulse; p/r/v have their own styles.
  for (const f of view.food) {
    if (f.k === "g") {
      ctx.fillStyle = "#5b7";
      ctx.beginPath();
      ctx.arc(f.x, f.y, 4, 0, Math.PI * 2);
      ctx.fill();
    } else if (f.k === "b") {
      // Blue super-food: pulsing cyan with a soft outer ring.
      const phase = (Math.sin(tNow * 4 + f.id) + 1) / 2; // 0..1
      const r = 8 + phase * 4;
      ctx.fillStyle = "rgba(80,200,255,0.18)";
      ctx.beginPath();
      ctx.arc(f.x, f.y, r + 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#3cf";
      ctx.beginPath();
      ctx.arc(f.x, f.y, r, 0, Math.PI * 2);
      ctx.fill();
    } else if (f.k === "y") {
      // Yellow power-up: rapid pulse + bright glow.
      const phase = (Math.sin(tNow * 6 + f.id) + 1) / 2;
      const r = 9 + phase * 5;
      ctx.fillStyle = "rgba(255,220,80,0.22)";
      ctx.beginPath();
      ctx.arc(f.x, f.y, r + 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#fd4";
      ctx.beginPath();
      ctx.arc(f.x, f.y, r, 0, Math.PI * 2);
      ctx.fill();
    } else if (f.k === "p") {
      // Purple slayer: pulsing.
      const phase = (Math.sin(tNow * 5 + f.id) + 1) / 2;
      const r = 9 + phase * 5;
      ctx.fillStyle = "rgba(170,80,255,0.22)";
      ctx.beginPath();
      ctx.arc(f.x, f.y, r + 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#a3f";
      ctx.beginPath();
      ctx.arc(f.x, f.y, r, 0, Math.PI * 2);
      ctx.fill();
    } else if (f.k === "r") {
      // Red speed: pulsing.
      const phase = (Math.sin(tNow * 5 + f.id) + 1) / 2;
      const r = 9 + phase * 5;
      ctx.fillStyle = "rgba(255,80,80,0.22)";
      ctx.beginPath();
      ctx.arc(f.x, f.y, r + 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#f55";
      ctx.beginPath();
      ctx.arc(f.x, f.y, r, 0, Math.PI * 2);
      ctx.fill();
    } else if (f.k === "v") {
      // Vacuum: animated outline only, rotating dashes.
      ctx.save();
      ctx.strokeStyle = "rgba(230,230,230,0.85)";
      ctx.lineWidth = 2 * cam.zoom;
      ctx.setLineDash([6, 4]);
      ctx.lineDashOffset = -(tNow * 30) % 10;
      ctx.beginPath();
      ctx.arc(f.x, f.y, 9, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }
  }

  // Cells — others first, then own (so own renders on top).
  const sorted = [...view.cells].sort((a, b) => (a.pid === ownPid ? 1 : 0) - (b.pid === ownPid ? 1 : 0));
  for (const c of sorted) {
    // pw aura — animated yellow ring drawn under the cell.
    if (c.pw) {
      const aPhase = (Math.sin(tNow * 5) + 1) / 2;
      const aR = c.r + 6 + aPhase * 6;
      ctx.strokeStyle = `rgba(255,220,80,${0.55 + aPhase * 0.35})`;
      ctx.lineWidth = 4 * cam.zoom;
      ctx.beginPath();
      ctx.arc(c.x, c.y, aR, 0, Math.PI * 2);
      ctx.stroke();
    }
    if (c.om) {
      // Purple omnivore aura.
      ctx.strokeStyle = `rgba(170,80,255,0.45)`;
      ctx.lineWidth = 5 * cam.zoom;
      ctx.beginPath();
      ctx.arc(c.x, c.y, c.r + 12, 0, Math.PI * 2);
      ctx.stroke();
    }
    if (c.sp) {
      // Red speed aura.
      ctx.strokeStyle = `rgba(255,80,80,0.4)`;
      ctx.lineWidth = 4 * cam.zoom;
      ctx.beginPath();
      ctx.arc(c.x, c.y, c.r + 8, 0, Math.PI * 2);
      ctx.stroke();
    }
    // Vacuum has no border ring — the visible food being pulled inward
    // already telegraphs the effect.
    ctx.fillStyle = c.pid === ownPid ? "#4af" : "#d65";
    ctx.beginPath();
    ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
    ctx.fill();
    if (c.nick) {
      ctx.fillStyle = "#fff";
      const px = Math.max(12, c.r * 0.4);
      ctx.font = `${px}px sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(c.nick, c.x, c.y);
    }
  }

  ctx.restore();
}

export function screenToWorld(canvas: HTMLCanvasElement, sx: number, sy: number, cam: Camera): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const cssW = rect.width;
  const cssH = rect.height;
  return {
    x: cam.x + (sx - cssW / 2) * cam.zoom,
    y: cam.y + (sy - cssH / 2) * cam.zoom,
  };
}
